/**
 * Immobilien – Unterseite für Immozwerg
 * Design: Warm, hell, cremeweiß (#FAF8F5), Violett als Akzent, Gold als Highlight
 *
 * Google Sheets Spalten (A–V):
 *  A  Objekt-ID
 *  B  Straße
 *  C  Ort
 *  D  PLZ
 *  E  Typ (Haus/Whg/Grundstück/MFH)
 *  F  Kaufpreis (€)
 *  G  Wohnfläche (m²)
 *  H  Grundstück (m²)
 *  I  Zimmer
 *  J  Baujahr
 *  K  Energieklasse
 *  L  Kurzbeschreibung
 *  M  Exposé-Landingpage (CRM)
 *  N  360°-Rundgang
 *  O  Bild-URL (Hauptbild)
 *  P  GO (Entwurf)
 *  Q  Freigabe (Live)
 *  R  Online-Status
 *  S  Status (Verfügbar/Reserviert/Verkauft)
 *  T  Objekttitel
 *  U  Nutzungsart
 *  V  Objektbeschreibung (Markdown-Link)
 */
import { useState, useEffect } from "react";
import { useSSRData } from "@/contexts/SSRDataContext";
import { normalizePropertyMarkdownUrl } from "@shared/property-markdown";
import { isPublishedProperty } from "@shared/property-publication";
import { parseCsvRows } from "@shared/csv";
import { sortProperties, type PropertySort } from "@shared/property-sorting";
import { matchesPropertySearch } from "@shared/property-search";
import SiteHeader from "@/components/SiteHeader";
import { Link } from "wouter";
import { ArrowLeft, MapPin, Euro, Maximize2, BedDouble, Phone, ExternalLink, Loader2, AlertCircle, Video, Home, Calendar, Search, Heart, X, Share2, ArrowDownUp } from "lucide-react";
import { buildImmobilienSlug } from "@/pages/ImmobilienDetail";
import SEO from "@/components/SEO";
import { useWhatsAppPopup } from "@/components/WhatsAppPopup";
import { useImmobilienFavoriten } from "@/hooks/useImmobilienFavoriten";
import { useLocale } from "@/contexts/LocaleContext";


// ─── Google Sheets Konfiguration ──────────────────────────────────────────────
const SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/1cHIo3k-AsCrlawe7u2YgGnVUGb5kiDQqivskU_ph-1U/export?format=csv&gid=0";

// ─── Typen ────────────────────────────────────────────────────────────────────
interface Immobilie {
  id: string;
  objekttitel: string;
  strasse: string;
  ort: string;
  plz: string;
  typ: string;
  preis: string;
  wohnflaeche: string;
  grundstueck: string;
  zimmer: string;
  baujahr: string;
  energieklasse: string;
  beschreibung: string;
  link: string;
  rundgang_url: string;
  bild_url: string;
  status: string; // Verfügbar | Reserviert | Verkauft
  online_status: string;
  nutzungsart: string; // Vermietet | Leerstand | Eigennutz
  markdown_url: string;
}

// ─── Hilfsfunktion: Prüft ob ein Wert eine gültige URL ist ─────────────────────
function isValidUrl(value: string): boolean {
  if (!value) return false;
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith("http://") || trimmed.startsWith("https://");
}

// ─── Google Drive Bild-URL konvertieren – nutzt Proxy um CORS-Blockade zu umgehen ─────
function convertDriveUrl(url: string): string {
  if (!url) return "";
  // https://drive.google.com/file/d/FILE_ID/view?usp=sharing → FILE_ID extrahieren
  const match = url.match(/\/file\/d\/([^/]+)\//);  
  if (match) {
    return `/api/drive-image?id=${match[1]}`;
  }
  // https://drive.google.com/open?id=FILE_ID oder ?id=FILE_ID
  const match2 = url.match(/[?&]id=([^&]+)/);
  if (match2) {
    return `/api/drive-image?id=${match2[1]}`;
  }
  return url;
}

function parseCSV(csv: string): Immobilie[] {
  const rows = parseCsvRows(csv);
  if (rows.length < 2) return [];
  const [headers, ...propertyRows] = rows;
  const markdownIndex = headers.findIndex((header) => /markdown/i.test(header));

  return propertyRows.map(v => {
    // Positionsbasiertes Mapping (A=0, B=1, ... T=19)
    const online_status = v[17] || "";

    // Nur ausdrücklich veröffentlichte Sheet-Objekte anzeigen. Dadurch können
    // Entwürfe und historische Platzhalter nie eigene öffentliche URLs erzeugen.
    if (!isPublishedProperty(v[15], v[16], online_status)) return null;

    const id = v[0] || "";
    if (!id) return null;

    return {
      id,
      strasse: v[1] || "",
      ort: v[2] || "",
      plz: v[3] || "",
      typ: v[4] || "",
      preis: v[5] || "",
      wohnflaeche: v[6] || "",
      grundstueck: v[7] || "",
      zimmer: v[8] || "",
      baujahr: v[9] || "",
      energieklasse: v[10] || "",
      beschreibung: v[11] || "",
      link: isValidUrl(v[12] || "") ? v[12] : "",
      rundgang_url: isValidUrl(v[13] || "") ? v[13] : "",
      // Google Sheet (Spalte O) ist die einzige Quelle
      bild_url: convertDriveUrl(v[14] || ""),
      online_status,
      // Status: Spalte S hat Vorrang; wenn leer, Spalte R als Fallback nutzen
      status: v[18] || (online_status === "Verkauft" ? "Verkauft" : "Verfügbar"),
      objekttitel: v[19] || (v[1] ? `${v[1]}, ${v[2]}` : v[2]) || "",
      nutzungsart: v[20] || "",
      markdown_url: normalizePropertyMarkdownUrl(v[markdownIndex >= 0 ? markdownIndex : 21] || ""),
    } as Immobilie;
  }).filter((o): o is Immobilie => o !== null);
}

// ─── Status-Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const { t } = useLocale();
  const styles: Record<string, string> = {
    "Verfügbar": "bg-green-100 text-green-700 border-green-200",
    "Reserviert": "bg-yellow-100 text-yellow-700 border-yellow-200",
    "Verkauft": "bg-gray-100 text-gray-700 border-gray-200",
  };
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${styles[status] || styles["Verfügbar"]}`}>
      {status === "Reserviert" ? t("reserved") : status === "Verkauft" ? t("sold") : t("available")}
    </span>
  );
}

// ─── Typ-Kategorisierung ─────────────────────────────────────────────────────
const HAUS_TYPEN = ["EFH", "MFH", "ZFH", "DHH", "RH"];
const WOHNUNG_TYPEN = ["Etagenwohnung", "EG-Wohnung", "DG-Wohnung", "DG Whg", "EG Whg", "Maisonette", "Penthouse", "Apartment"];

function isHausTyp(typ: string): boolean {
  return HAUS_TYPEN.includes(typ) || typ.toLowerCase().includes("haus");
}
function isWohnungTyp(typ: string): boolean {
  return WOHNUNG_TYPEN.includes(typ) || typ.toLowerCase().includes("wohnung") || typ.toLowerCase().includes("etw");
}
function isGrundstueckTyp(typ: string): boolean {
  return typ.toLowerCase().includes("grundst");
}

// ─── Immobilien-Karte ─────────────────────────────────────────────────────────
export function ImmobilienKarte({ obj, isFavorit, onToggleFavorit }: { obj: Immobilie; isFavorit?: boolean; onToggleFavorit?: (id: string) => void }) {
  const { t, locale } = useLocale();
  const hatGrundstueck = isHausTyp(obj.typ) && obj.grundstueck;
  const imageUrl = obj.bild_url?.includes("/api/drive-image")
    ? `${obj.bild_url}&w=800`
    : obj.bild_url;
  const exposeObjectName = obj.objekttitel || [obj.typ, obj.ort].filter(Boolean).join(" in ") || `Objekt ${obj.id}`;
  const exposeLinkText = `${t("expose")}: ${exposeObjectName}`;

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#e8dff0] hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 flex flex-col">
      {/* Bild – klickbar zur Detailseite */}
      <Link href={`/immobilien/${buildImmobilienSlug(obj.id, obj.ort, obj.typ)}`} className="block relative h-48 overflow-hidden cursor-pointer">
        <img
          src={imageUrl || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&q=80"}
          alt={obj.objekttitel || obj.ort}
          data-original-content
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          width="800"
          height="450"
          loading="lazy"
          decoding="async"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&q=80";
          }}
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <StatusBadge status={obj.status} />
          {onToggleFavorit && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorit(obj.id); }}
              className="bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-sm transition-all hover:scale-110 self-start"
              title={isFavorit ? t("removeFavorite") : t("saveFavorite")}
              aria-label={isFavorit ? t("removeFavorite") : t("saveFavorite")}
            >
              <Heart size={16} className={isFavorit ? "fill-[#a066c2] text-[#a066c2]" : "text-[#b09ec0]"} />
            </button>
          )}
        </div>
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          <div className="bg-[#2d1b4e]/85 backdrop-blur-sm rounded-lg px-2.5 py-1">
            <span className="text-xs font-bold text-white">{t("objectNumber")} {obj.id}</span>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-2.5 py-1">
            <span className="text-xs font-bold text-[#2d1b4e]" data-original-content>{obj.typ}</span>
          </div>
          {obj.nutzungsart && (
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-2.5 py-1">
              <span className="text-xs font-semibold text-[#631172]" data-original-content>{obj.nutzungsart}</span>
            </div>
          )}
        </div>
      </Link>

      {/* Inhalt */}
      <div className="p-5 flex flex-col flex-1">
        {/* Objekttitel */}
        {obj.objekttitel && (
          <h3 className="font-bold text-[#2d1b4e] mb-1 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span data-original-content>{obj.objekttitel}</span>
          </h3>
        )}

        {/* Standort: PLZ + Ort */}
        <div className="flex items-center gap-1.5 text-[#6b3f86] text-sm mb-3">
          <MapPin size={13} />
          <span data-original-content>{obj.plz && obj.ort ? `${obj.plz} ${obj.ort}` : obj.ort || obj.plz}</span>
        </div>

        {/* Kennzahlen */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
          {obj.zimmer && (
            <div className="flex items-center gap-1.5 text-sm text-[#6b5b7e]">
              <BedDouble size={14} className="text-[#a066c2]" />
              <span>{obj.zimmer} {t("rooms")}</span>
            </div>
          )}
          {obj.wohnflaeche && (
            <div className="flex items-center gap-1.5 text-sm text-[#6b5b7e]">
              <Maximize2 size={14} className="text-[#a066c2]" />
              <span>{obj.wohnflaeche} m² {t("livingArea")}</span>
            </div>
          )}
          {hatGrundstueck && (
            <div className="flex items-center gap-1.5 text-sm text-[#6b5b7e]">
              <Home size={14} className="text-[#a066c2]" />
              <span>{obj.grundstueck} m² {t("landArea")}</span>
            </div>
          )}
          {obj.baujahr && (
            <div className="flex items-center gap-1.5 text-sm text-[#6b5b7e]">
              <Calendar size={14} className="text-[#a066c2]" />
              <span>{t("yearBuilt")} {obj.baujahr}</span>
            </div>
          )}
        </div>

        {obj.beschreibung && (
          <p className="text-xs text-[#6b5b7e] leading-relaxed mb-4 flex-1 line-clamp-3" data-original-content>{obj.beschreibung}</p>
        )}

        {/* Preis + CTA */}
        <div className="mt-auto pt-3 border-t border-[#f0eaf8]">
          <div className="flex items-center justify-between mb-2">
            <div>
              {obj.status === "Verkauft" ? (
                <div className="font-bold text-[#631172] text-sm flex items-center gap-1.5">
                  <span>🏠</span> {t("newOwners")}
                </div>
              ) : (
                <>
                  <div className="text-xs text-[#6b3f86] font-medium">{t("price")}</div>
                  <div className="font-bold text-[#2d1b4e] text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {obj.preis ? Number(obj.preis.replace(/\D/g, "")).toLocaleString(locale === "de" ? "de-DE" : locale) + " € VHB" : t("priceOnRequest")}
                  </div>
                </>
              )}
            </div>
            {/* Zum Exposé – externer Link zu Haushirsch (Spalte M) */}
            {obj.status !== "Verkauft" && obj.link && (
              <a href={obj.link} target="_blank" rel="noopener noreferrer"
                aria-label={`${exposeLinkText} (öffnet in neuem Tab)`}
                title={`${exposeLinkText} (öffnet in neuem Tab)`}
                className="flex min-w-0 items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
                <span className="max-w-36 truncate">{exposeLinkText}</span> <ExternalLink size={11} className="shrink-0" aria-hidden="true" />
              </a>
            )}
          </div>
          {/* Teilen-Button */}
          <button
            onClick={() => {
              const url = `${window.location.origin}/immobilien/${buildImmobilienSlug(obj.id, obj.ort, obj.typ)}`;
              if (navigator.share) {
                navigator.share({ title: obj.objekttitel || obj.ort, url });
              } else {
                navigator.clipboard?.writeText(url).then(() => alert("Link kopiert!"));
              }
            }}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-full text-xs font-bold mt-2 transition-all hover:scale-105 border-2"
            style={{ borderColor: "#e8dff0", color: "#6b5b7e", background: "transparent" }}
            title={t("share")}
            aria-label={t("share")}>
            <Share2 size={12} /> {t("share")}
          </button>
          {/* 360°-Rundgang Button – nur wenn URL vorhanden und nicht verkauft */}
          {obj.rundgang_url && obj.status !== "Verkauft" && (
            <a href={obj.rundgang_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 border-2"
              style={{ borderColor: "#a066c2", color: "#631172", background: "transparent" }}>
              <Video size={13} /> {t("tour")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Hook: Immobilien aus Google Sheets laden ─────────────────────────────────
export function useImmobilien() {
  const ssrData = useSSRData();
  const hasSSRData = !!(ssrData?.immobilien && ssrData.immobilien.length > 0);

  const [data, setData] = useState<Immobilie[]>(() => {
    if (hasSSRData) {
      // SSR-Daten direkt verwenden; dadurch bleibt die Google-Sheets-Automation
      // unverändert, aber die bereits serverseitig geladene Startseitenliste löst
      // im Browser keinen zweiten, render-kritischen Fetch aus.
      return ssrData!.immobilien!.map(item => ({
        ...item,
        bild_url: item.bild_url ? convertDriveUrl(item.bild_url) : "",
      })) as Immobilie[];
    }
    return [];
  });
  const [loading, setLoading] = useState(!hasSSRData);
  const [error, setError] = useState(false);

  useEffect(() => {
    // SSR-Daten zeichnen die Seite ohne wartenden Erst-Fetch. Anschließend wird
    // dennoch gegen die unveränderte CSV-Quelle revalidiert: Neue freigegebene
    // Zeilen sollen ohne Server-Cache-Ablauf auch auf der Startseite erscheinen.
    if (hasSSRData) {
      setLoading(false);
    }

    let cancelled = false;
    fetch(`${SHEET_CSV_URL}&cacheBust=${Date.now()}`, { cache: "no-store" })
      .then(r => {
        if (!r.ok) throw new Error(`Google-Sheets-Abruf fehlgeschlagen: ${r.status}`);
        return r.text();
      })
      .then(csv => {
        if (cancelled) return;
        const parsed = parseCSV(csv);
        setData(parsed);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Bei SSR-Daten bleibt die bereits sichtbare Liste erhalten. Ohne SSR
        // zeigen wir wie bisher den korrekten Fehlerzustand statt leerer Daten.
        if (!hasSSRData) {
          setData([]);
          setError(true);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasSSRData]);

  return { data, loading, error };
}

export default function Immobilien() {
  const { data, loading, error } = useImmobilien();
  const { t } = useLocale();
  const [filter, setFilter] = useState("Alle");
  const [suche, setSuche] = useState("");
  const [sortierung, setSortierung] = useState<PropertySort>("newest");
  const { favoriten, toggle: toggleFavorit } = useImmobilienFavoriten();
  const { openWhatsApp, popupElement } = useWhatsAppPopup();

  // Vereinfachter Filter: nur 4 Oberkategorien + Favoriten
  const kategorien = [
    "Alle",
    ...(data.some(o => isHausTyp(o.typ)) ? ["Häuser"] : []),
    ...(data.some(o => isWohnungTyp(o.typ)) ? ["Wohnungen"] : []),
    ...(data.some(o => isGrundstueckTyp(o.typ)) ? ["Grundstücke"] : []),
    ...(favoriten.length > 0 ? ["Favoriten"] : []),
  ];

  // Neueste zuerst (letzte Zeile im Sheet = oben)
  const sortedData = [...data].reverse();

  // Typ-Filter
  const typFiltered = filter === "Alle" ? sortedData
    : filter === "Häuser" ? sortedData.filter(o => isHausTyp(o.typ))
    : filter === "Wohnungen" ? sortedData.filter(o => isWohnungTyp(o.typ))
    : filter === "Grundstücke" ? sortedData.filter(o => isGrundstueckTyp(o.typ))
    : filter === "Favoriten" ? sortedData.filter(o => favoriten.includes(o.id))
    : sortedData.filter(o => o.typ === filter);

  // Suchfilter (Ort, PLZ, Titel, Typ, Beschreibung)
  const suchbegriff = suche.trim().toLowerCase();
  const filtered = suchbegriff
    ? typFiltered.filter((property) => matchesPropertySearch(property, suchbegriff))
    : typFiltered;
  const sorted = sortProperties(filtered, sortierung);

  return (
    <>
      {popupElement}
      <SEO
        title="Immobilien kaufen Brackenheim & Güglingen | Immozwerg"
        description="Aktuelle Immobilienangebote in Brackenheim, Güglingen, Heilbronn & Zabergäu. Häuser, Wohnungen und Grundstücke von Immozwerg – Andrea Heinz."
        keywords="Immobilien kaufen Brackenheim, Haus kaufen Brackenheim, Wohnung kaufen Brackenheim, Immobilien Güglingen, Haus kaufen Güglingen, Immobilien Heilbronn kaufen, Immobilien Zabergäu, Grundstück kaufen Brackenheim"
      />

      <div className="min-h-screen" style={{ background: "#FAF8F5" }}>
        <SiteHeader />

        <div className="max-w-5xl mx-auto px-4 py-10">
          {/* Titel */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#2d1b4e] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Aktuelle Immobilien
            </h1>
            <p className="text-[#6b5b7e]">Alle Objekte im Raum Heilbronn–Stuttgart – direkt von Immozwerg.</p>
          </div>

          {/* Suchfeld */}
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#a066c2] pointer-events-none" />
            <input
              type="text"
              value={suche}
              onChange={e => setSuche(e.target.value)}
              placeholder="Suche nach Ort, PLZ oder Stichwort …"
              className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-[#e8dff0] bg-white text-sm text-[#2d1b4e] placeholder-[#b09ec0] focus:outline-none focus:ring-2 focus:ring-[#a066c2]/30 focus:border-[#a066c2] transition-all"
            />
            {suche && (
              <button onClick={() => setSuche("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b09ec0] hover:text-[#631172]">
                <X size={15} />
              </button>
            )}
          </div>

          {/* Filter */}
          {kategorien.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {kategorien.map((t: string) => (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                    filter === t
                      ? "text-white border-transparent"
                      : "text-[#631172] border-[#a066c2] bg-transparent hover:bg-[#f5eeff]"
                  }`}
                  style={filter === t ? { background: "linear-gradient(135deg, #631172, #a066c2)" } : {}}
                >
                  {t === "Favoriten" && <Heart size={13} className={filter === t ? "fill-white" : "fill-[#a066c2]"} />}
                  {t}
                </button>
              ))}
              {favoriten.length > 0 && (
                <Link href="/merkliste" className="ml-auto inline-flex min-h-11 items-center gap-2 rounded-full border border-[#631172] px-4 py-1.5 text-sm font-bold text-[#631172] hover:bg-[#f5eeff]">
                  <Heart size={14} className="fill-[#a066c2] text-[#a066c2]" aria-hidden="true" /> {t("viewFavorites")}
                </Link>
              )}
            </div>
          )}

          {!loading && data.length > 0 && (
            <div className="mb-8 flex justify-end">
              <label className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#e8dff0] bg-white px-3 text-sm font-medium text-[#2d1b4e] shadow-sm">
                <ArrowDownUp size={16} className="text-[#631172]" aria-hidden="true" />
                <span className="sr-only">Immobilien sortieren</span>
                <select
                  value={sortierung}
                  onChange={(event) => setSortierung(event.target.value as PropertySort)}
                  aria-label="Immobilien sortieren"
                  className="cursor-pointer bg-transparent pr-1 font-semibold outline-none"
                >
                  <option value="newest">Neueste zuerst</option>
                  <option value="price-asc">Preis: niedrig zuerst</option>
                  <option value="price-desc">Preis: hoch zuerst</option>
                  <option value="rooms-asc">Zimmer: wenig zuerst</option>
                  <option value="rooms-desc">Zimmer: viel zuerst</option>
                </select>
              </label>
            </div>
          )}

          {/* Ladezustand */}
          {loading && (
            <div className="flex items-center justify-center py-20 gap-3 text-[#a066c2]">
              <Loader2 size={24} className="animate-spin" />
              <span>Objekte werden geladen…</span>
            </div>
          )}

          {/* Fehler */}
          {error && !loading && (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm">
              <AlertCircle size={16} />
              <span>Objekte konnten nicht geladen werden. Bitte später erneut versuchen.</span>
            </div>
          )}

          {/* Karten-Grid */}
          {!loading && (
            <>
              {sorted.length === 0 ? (
                <div className="text-center py-20 text-[#6b5b7e]">
                  <Home size={40} className="mx-auto mb-4 text-[#e8dff0]" />
                  <p className="font-medium">Aktuell keine Objekte in dieser Kategorie.</p>
                  <p className="text-sm mt-1">Schreib mir – ich finde auch Off-Market-Objekte für dich.</p>
                  <Link href="/kontakt">
                    <button
                      className="mt-4 px-6 py-2 rounded-full text-sm font-bold text-white transition-all hover:scale-105"
                      style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}
                    >
                      Jetzt anfragen
                    </button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sorted.map(obj => (
                    <ImmobilienKarte key={obj.id} obj={obj} isFavorit={favoriten.includes(obj.id)} onToggleFavorit={toggleFavorit} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* CTA unten */}
          {!loading && sorted.length > 0 && (
            <div className="mt-12 text-center bg-white rounded-2xl border border-[#e8dff0] p-8">
              <h2 className="text-xl font-bold text-[#2d1b4e] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Dein Wunschobjekt nicht dabei?
              </h2>
              <p className="text-[#6b5b7e] text-sm mb-4">Ich habe auch Zugang zu Off-Market-Objekten und helfe dir beim Kauf.</p>
              <Link href="/kontakt">
                <button
                  className="px-6 py-2.5 rounded-full text-sm font-bold text-white transition-all hover:scale-105"
                  style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}
                >
                  Jetzt anfragen
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
